import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';

/**
 * Parses text with markdown-style links [text](url) into React elements
 * @param {string} text - Text with optional [link text](/path) syntax
 * @returns {React.ReactNode[]} Array of text and Link elements
 */
const parseTextWithLinks = (text) => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const [, linkText, linkUrl] = match;

    // Check if it's an external link or internal route
    if (linkUrl.startsWith('http')) {
      parts.push(
        <a
          key={match.index}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-600 underline underline-offset-2"
        >
          {linkText}
        </a>
      );
    } else {
      parts.push(
        <Link
          key={match.index}
          to={linkUrl}
          className="text-blue-500 hover:text-blue-600 underline underline-offset-2"
        >
          {linkText}
        </Link>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

/**
 * A reusable help/tips modal component for page-specific guidance
 *
 * @param {Object} props
 * @param {Array<{text: string, mobileOnly?: boolean, desktopOnly?: boolean}>} props.tips - Array of tip objects
 * @param {string} props.title - Modal title (optional, defaults to "Tips")
 * @param {React.ReactNode} props.trigger - Custom trigger element (optional)
 * @param {string} props.triggerClassName - Additional classes for the trigger button
 */
export default function PageHelpModal({
  tips = [],
  title = "Tips",
  trigger,
  triggerClassName = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const overlayRef = useRef(null);

  // Detect desktop/mobile
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (e) => setIsDesktop(e.matches);
    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      setIsOpen(false);
    }
  };

  // Filter tips based on device
  const filteredTips = tips.filter(tip => {
    if (tip.mobileOnly && isDesktop) return false;
    if (tip.desktopOnly && !isDesktop) return false;
    return true;
  });

  return (
    <>
      {/* Trigger */}
      {trigger ? (
        <span onClick={() => setIsOpen(true)}>{trigger}</span>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className={`inline-flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors animate-pulse ${triggerClassName}`}
          aria-label="Help"
        >
          <HelpCircle size={20} />
        </button>
      )}

      {/* Modal */}
      {isOpen && (
        <div
          ref={overlayRef}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-50 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly"
          aria-modal="true"
          role="dialog"
        >
          <div
            className="absolute inset-0 z-0"
            onClick={() => setIsOpen(false)}
          />
          <div className="relative z-10 bg-white/95 backdrop-blur-md rounded-2xl max-w-md w-full p-6 shadow-soft-xl border border-rose-pastel-100/50 animate-slideUp">
            {/* Header */}
            <div className="mb-4 lg:mb-6">
              <h3 className="text-lg lg:text-xl font-display font-semibold text-slate-800 mb-2 text-left">
                {title}
              </h3>
              <div className="w-16 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full"></div>
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-3 right-3 w-9 h-9 text-slate-600 hover:text-slate-800 flex items-center justify-center"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tips List */}
            <ul className="space-y-3 text-left">
              {filteredTips.map((tip, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-slate-600"
                >
                  <span className="flex-shrink-0 text-base font-bold text-rose-pastel-600 leading-relaxed">
                    {index + 1}.
                  </span>
                  <span className="text-sm leading-relaxed">
                    {parseTextWithLinks(tip.text)}
                  </span>
                </li>
              ))}
            </ul>

            {/* Close button */}
            <div className="mt-6">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-rose-pastel-300 to-lavender-300 hover:from-rose-pastel-400 hover:to-lavender-400 text-white font-medium shadow-soft transition-all"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
